import api from '@/shared/api/api';
import { uploadFileToSignedUrl } from '@/shared/lib/uploadSignedUrl';
import type {
  CreatePresetPayload,
  GetPresetsParams,
  Preset,
  PresetResponse,
  PaginatedPresetsResponse,
  GalleryItem,
  UploadGalleriesResponse,
  UpdatePresetPayload,
} from '../types';

export interface SignedUploadResponse {
  signedUploadUrl: string;
  path: string;
  token: string;
  publicUrl: string;
}

export const presetApi = {
  list: async (
    params?: GetPresetsParams,
    signal?: AbortSignal
  ): Promise<PaginatedPresetsResponse> => {
    const mapped: Record<string, unknown> = {};
    const p = params ?? {};
    if (p.page !== undefined) mapped.page = p.page;
    if (p.limit !== undefined) mapped.limit = p.limit;
    if (p.name !== undefined) mapped.name = p.name;
    if (p.creator !== undefined) mapped.creator = p.creator;
    if (p.published !== undefined) mapped.published = p.published;
    if (p.categoryId !== undefined) mapped.categoryId = p.categoryId;
    if (p.isPaid !== undefined) mapped.isPaid = p.isPaid;
    if (p.sort !== undefined) mapped.sort = p.sort;
    return api<PaginatedPresetsResponse>({
      url: '/v1/presets',
      method: 'GET',
      params: mapped,
      signal,
    });
  },

  get: async (id: string, signal?: AbortSignal): Promise<Preset> => {
    return api<Preset>({
      url: `/v1/presets/${id}`,
      method: 'GET',
      signal,
    });
  },

  create: async (
    data: Omit<CreatePresetPayload, 'cover' | 'lut'>
  ): Promise<PresetResponse> => {
    return api<PresetResponse>({
      url: '/v1/presets',
      method: 'POST',
      data: data,
    });
  },

  update: async (
    id: string,
    data: Omit<UpdatePresetPayload, 'cover' | 'lut'>
  ): Promise<PresetResponse> => {
    return api<PresetResponse>({
      url: `/v1/presets?id=${id}`,
      method: 'POST',
      data: data,
    });
  },

  getCoverUploadUrl: async (id: string): Promise<SignedUploadResponse> => {
    return api<SignedUploadResponse>({
      url: `/v1/presets/${id}/cover`,
      method: 'PATCH',
    });
  },

  getLutUploadUrl: async (id: string): Promise<SignedUploadResponse> => {
    return api<SignedUploadResponse>({
      url: `/v1/presets/${id}/lut`,
      method: 'PATCH',
    });
  },

  uploadCover: async (presetId: string, file: File): Promise<string> => {
    const { signedUploadUrl, token, publicUrl } =
      await presetApi.getCoverUploadUrl(presetId);
    await uploadFileToSignedUrl(signedUploadUrl, token, file);
    return publicUrl;
  },

  uploadLut: async (presetId: string, file: File): Promise<string> => {
    const { signedUploadUrl, token, publicUrl } =
      await presetApi.getLutUploadUrl(presetId);
    await uploadFileToSignedUrl(signedUploadUrl, token, file);
    return publicUrl;
  },

  remove: async (id: string): Promise<{ id: string; deleted: boolean }> => {
    return api({ url: `/v1/presets/${id}`, method: 'DELETE' });
  },

  getGalleries: async (
    id: string,
    signal?: AbortSignal
  ): Promise<GalleryItem[]> => {
    return api<GalleryItem[]>({
      url: `/v1/presets/${id}/galleries`,
      method: 'GET',
      signal,
    });
  },

  uploadGalleries: async (
    id: string,
    galleries: GalleryItem[]
  ): Promise<UploadGalleriesResponse> => {
    return api<UploadGalleriesResponse>({
      url: `/v1/presets/${id}/galleries`,
      method: 'POST',
      data: { galleries },
    });
  },

  uploadGalleryItem: async (
    beforeFile: File,
    afterFile: File,
    data: {
      presetId: string;
      before: string;
      after: string;
      createdById: string;
      displayOrder: number;
    }
  ): Promise<void> => {
    const form = new FormData();
    form.append('beforeFile', beforeFile);
    form.append('afterFile', afterFile);
    form.append('data', JSON.stringify(data));
    return api({
      url: '/v1/preset-galleries',
      method: 'POST',
      data: form,
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },

  deleteGallery: async (galleryUuid: string): Promise<{ success: boolean }> => {
    return api({
      url: `/v1/preset-galleries/${galleryUuid}`,
      method: 'DELETE',
    });
  },
};
